import { useCallback, useEffect, useState } from "react";
import { useTeamContext } from "../contexts/TeamContext";
import { Aggregates } from "../rest/responses/Aggregates";
import { useRestClientContext } from "../contexts/RestClientContext";
import { Accessor } from "../types/types";
import { FilterType } from "./usePropertyFilters";
import { isDateString } from "../dates";

export interface UsePropertyAggregates {
	loading: boolean;
	aggregates?: Aggregates;
	getFilterTypeFromAggregates: (accessor: Accessor) => FilterType;
}

export default function usePropertyAggregates() {
	const api = useRestClientContext();
	const team = useTeamContext();
	const [loading, setLoading] = useState(true);
	const [aggregates, setAggregates] = useState<Aggregates>();

	const getFilterTypeFromAggregates = useCallback(
		(accessor: Accessor) => {
			if (!aggregates)
				throw new Error("Aggregates not ready. Make sure they are loaded before using this method.");

			if (accessor in aggregates.values) return FilterType.VALUES;

			// Date/time columns report their bounds as strings, everything else as numbers. Validate both sides.
			if (accessor in aggregates.mins)
				return isDateString(aggregates.mins[accessor]) && isDateString(aggregates.maxs[accessor])
					? FilterType.DATE_RANGE
					: FilterType.RANGE;

			return FilterType.PATTERN;
		},
		[aggregates]
	);

	useEffect(() => {
		setLoading(true);

		api.v3.getTeamAggregates(team).then((aggregates) => {
			setAggregates(aggregates);
			setLoading(false);
		});

		// NB: Don't want api.v3 as a dep it'll lead to endless reloading
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [team]);

	return { loading, aggregates, getFilterTypeFromAggregates };
}
